/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import type { PaymentTerminalV2, TerminalProvider } from "@lemmonhq/types";
import { useAuth } from "@app/vue/providers/Auth";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { getTerminalProviderConfig, type TerminalFieldKey } from "./providerConfig";
import { ProviderPicker } from "./ProviderPicker";
import { StaffPicker } from "./StaffPicker";
import { TerminalField } from "./TerminalField";
import { TerminalSummary } from "./TerminalSummary";
import { errorMessage } from "./utils";

export type PayrexxIntegrationOption = { id: string; label: string };

/** What a save did, so the list can update without refetching. */
export type TerminalChange =
  | { kind: "saved"; terminal: PaymentTerminalV2 }
  | { kind: "deleted"; id: number };

type TerminalFormProps = {
  mode: "create" | "edit";
  terminal: PaymentTerminalV2 | null;
  payrexxIntegrations: PayrexxIntegrationOption[];
  onSaved: (change: TerminalChange) => void;
  onClose: () => void;
};

type FieldValues = Record<TerminalFieldKey, string>;

const NS = "settings.terminals";

const initialFields = (terminal: PaymentTerminalV2 | null): FieldValues => ({
  serial_number: terminal?.serial_number || "",
  terminal_id: terminal?.terminal_id ? String(terminal.terminal_id) : "",
  password: terminal?.password || "",
  client_id: terminal?.client_id || "",
  client_secret: terminal?.client_secret || "",
  otp: "",
});

/**
 * Create / edit sheet for a payment terminal.
 *
 * Create: pick provider, then the provider's steps (Payrexx = details, then
 * pairing with serial + one-time code). Edit: provider, serial and status are
 * fixed facts shown in a summary; only name, staff and (for credential-based
 * providers like Nakka) the credentials stay editable. The pairing code is
 * never asked for again after pairing.
 */
export const TerminalForm = defineComponent<TerminalFormProps>({
  name: "TerminalForm",
  props: ["mode", "terminal", "payrexxIntegrations", "onSaved", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const auth = useAuth();

    const provider = ref<TerminalProvider>(
      (props.terminal?.provider as TerminalProvider) || "payrexx",
    );
    const name = ref(props.terminal?.name || "");
    // Older rows store numeric strings; normalise so the chips match.
    const staffIds = ref<number[]>((props.terminal?.staff_id || []).map(Number));
    const integrationId = ref(
      props.terminal?.integration_id || props.payrexxIntegrations[0]?.id || "",
    );
    const fields = ref<FieldValues>(initialFields(props.terminal));
    const step = ref(0);
    const submitting = ref(false);
    const confirmingDelete = ref(false);
    const formError = ref<string | null>(null);

    const colleagues = computed(() => auth.user.value?.colleagues || []);
    const config = computed(() => getTerminalProviderConfig(provider.value));
    const isEdit = computed(() => props.mode === "edit");
    const isPairing = computed(() => config.value.action === "pair");
    const isWizard = computed(
      () => !isEdit.value && config.value.steps.length > 1,
    );

    // Fields shown in the single-page form. Paired terminals are identified by
    // their serial, so nothing provider-specific is editable after pairing.
    const singleFormFields = computed(() =>
      isEdit.value && isPairing.value
        ? []
        : config.value.steps.flatMap((s) => s.fields).filter((f) => !f.otp),
    );

    const integrationLabel = computed(
      () =>
        props.payrexxIntegrations.find((i) => i.id === props.terminal?.integration_id)
          ?.label ?? null,
    );

    const setField = (key: TerminalFieldKey, raw: string) => {
      fields.value = {
        ...fields.value,
        [key]: key === "otp" ? raw.toUpperCase().replace(/\s/g, "") : raw,
      };
    };

    const requiredFilled = (keys: { key: TerminalFieldKey; required?: boolean }[]) =>
      keys.every((f) => !f.required || fields.value[f.key].trim() !== "");

    const detailsValid = computed(
      () =>
        name.value.trim() !== "" && (!isPairing.value || integrationId.value !== ""),
    );
    const canSubmit = computed(() => {
      if (isEdit.value) {
        return name.value.trim() !== "" && requiredFilled(singleFormFields.value);
      }
      const stepFields = isWizard.value
        ? (config.value.steps[step.value]?.fields ?? [])
        : singleFormFields.value;
      return detailsValid.value && requiredFilled(stepFields);
    });

    const terminalIdNumber = () => {
      const parsed = parseInt(fields.value.terminal_id, 10);
      return Number.isNaN(parsed) ? null : parsed;
    };

    const run = async (
      action: () => Promise<TerminalChange>,
      fallbackKey: string,
    ) => {
      if (submitting.value) return;
      submitting.value = true;
      formError.value = null;
      try {
        props.onSaved(await action());
      } catch (error) {
        formError.value = errorMessage(error, t(fallbackKey));
      } finally {
        submitting.value = false;
      }
    };

    const saved = (terminal: unknown): TerminalChange => ({
      kind: "saved",
      terminal: terminal as PaymentTerminalV2,
    });

    const submit = () => {
      if (!canSubmit.value) return;

      if (isEdit.value && props.terminal) {
        const terminal = props.terminal;
        return run(
          async () =>
            saved(await trpc.terminals.update.mutate({
              id: terminal.id,
              data: isPairing.value
                ? { name: name.value.trim(), staff_ids: staffIds.value }
                : {
                    name: name.value.trim(),
                    staff_ids: staffIds.value,
                    serial_number: fields.value.serial_number || null,
                    terminal_id: terminalIdNumber(),
                    password: fields.value.password || undefined,
                    client_id: fields.value.client_id || null,
                    client_secret: fields.value.client_secret || null,
                  },
            })),
          `${NS}.save_failed`,
        );
      }

      if (isPairing.value) {
        return run(
          async () =>
            saved(await trpc.terminals.pair.mutate({
              name: name.value.trim(),
              integration_id: integrationId.value,
              serial_number: fields.value.serial_number.trim(),
              otp: fields.value.otp,
              staff_ids: staffIds.value,
            })),
          `${NS}.pair_failed`,
        );
      }

      return run(
        async () =>
          saved(await trpc.terminals.create.mutate({
            name: name.value.trim(),
            provider: provider.value,
            serial_number: fields.value.serial_number || null,
            terminal_id: terminalIdNumber(),
            password: fields.value.password || undefined,
            client_id: fields.value.client_id || null,
            client_secret: fields.value.client_secret || null,
            staff_ids: staffIds.value,
          })),
        `${NS}.save_failed`,
      );
    };

    // A terminal still paired at its provider (Payrexx "paired", Nakka
    // "connected") must be unpaired before it can be deleted.
    const isPaired = computed(() =>
      ["paired", "connected"].includes(props.terminal?.status ?? ""),
    );

    const remove = () => {
      if (!props.terminal) return;
      if (!confirmingDelete.value) {
        confirmingDelete.value = true;
        return;
      }
      const id = props.terminal.id;
      if (isPaired.value) {
        return run(
          async () => saved(await trpc.terminals.unpair.mutate({ id })),
          `${NS}.unpair_failed`,
        );
      }
      return run(async () => {
        await trpc.terminals.delete.mutate({ id });
        return { kind: "deleted", id };
      }, `${NS}.delete_failed`);
    };

    const removeLabelKey = computed(() => {
      if (isPaired.value) {
        return confirmingDelete.value ? `${NS}.confirm_unpair` : `${NS}.unpair`;
      }
      return confirmingDelete.value ? `${NS}.confirm_delete` : `${NS}.delete`;
    });

    // An unpaired Payrexx or Nakka terminal can be paired again in place with
    // a new one-time code from the device; name and staff stay as they are.
    const canRepair = computed(
      () =>
        isEdit.value &&
        !isPaired.value &&
        ["payrexx", "nakka"].includes(props.terminal?.provider ?? "nakka"),
    );
    const repairOtp = ref("");

    const repair = () => {
      if (!props.terminal || repairOtp.value === "") return;
      const id = props.terminal.id;
      const otp = repairOtp.value;
      return run(
        async () => saved(await trpc.terminals.repair.mutate({ id, otp })),
        `${NS}.pair_failed`,
      );
    };

    // ── Sections ──────────────────────────────────────────────────────────────

    const renderIntegrationSelect = () => (
      <label class="form-control w-full">
        <div class="label">
          <span class="label-text text-sm font-medium text-base-content/80">
            {t(`${NS}.integration`)}
          </span>
        </div>
        {props.payrexxIntegrations.length > 0 ? (
          <select
            class="select select-bordered w-full"
            value={integrationId.value}
            onChange={(e: Event) => {
              integrationId.value = (e.target as HTMLSelectElement).value;
            }}
          >
            <option value="" disabled>
              {t(`${NS}.integration_placeholder`)}
            </option>
            {props.payrexxIntegrations.map((i) => (
              <option key={i.id} value={i.id}>
                {i.label}
              </option>
            ))}
          </select>
        ) : (
          <div role="alert" class="alert alert-warning text-sm">
            <span class="material-symbols-outlined">warning</span>
            <span>{t(`${NS}.no_payrexx_integration`)}</span>
          </div>
        )}
      </label>
    );

    const renderNameField = () => (
      <label class="form-control w-full">
        <div class="label">
          <span class="label-text text-sm font-medium text-base-content/80">
            {t(`${NS}.display_name`)}
          </span>
        </div>
        <input
          type="text"
          value={name.value}
          onInput={(e: Event) => {
            name.value = (e.target as HTMLInputElement).value;
          }}
          placeholder={t(`${NS}.display_name_placeholder`)}
          class="input input-bordered w-full"
        />
      </label>
    );

    const renderFields = (list: typeof singleFormFields.value) =>
      list.length > 0 && (
        <div class="space-y-1">
          {list.map((field) => (
            <TerminalField
              key={field.key}
              field={field}
              value={fields.value[field.key]}
              onUpdate={(v: string) => setField(field.key, v)}
            />
          ))}
        </div>
      );

    const renderStaff = () => (
      <StaffPicker
        colleagues={colleagues.value}
        selectedIds={staffIds.value}
        onChange={(ids: number[]) => {
          staffIds.value = ids;
        }}
      />
    );

    const renderInstructions = (keys: string[]) => (
      <ol class="space-y-3 rounded-xl border border-base-300 bg-base-200/40 p-4">
        {keys.map((key, i) => (
          <li key={key} class="flex gap-3 text-sm text-base-content/80">
            <span class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
              {i + 1}
            </span>
            <span class="pt-0.5">{t(key)}</span>
          </li>
        ))}
      </ol>
    );

    const renderRepair = () => (
      <div class="space-y-3 rounded-xl border border-base-300 bg-base-200/40 p-4">
        <div>
          <div class="text-sm font-semibold text-base-content">
            {t(`${NS}.pair_again`)}
          </div>
          <div class="text-xs text-base-content/60">
            {t(`${NS}.pair_again_hint`)}
          </div>
        </div>
        <TerminalField
          field={{
            key: "otp",
            labelKey: `${NS}.otp`,
            placeholderKey: `${NS}.otp_placeholder`,
            mono: true,
            required: true,
            otp: true,
          }}
          value={repairOtp.value}
          onUpdate={(v: string) => {
            repairOtp.value = v.toUpperCase().replace(/\s/g, "");
          }}
        />
        <button
          type="button"
          class="btn btn-primary btn-sm w-full"
          disabled={submitting.value || repairOtp.value === ""}
          onClick={repair}
        >
          {submitting.value && <span class="loading loading-spinner loading-sm"></span>}
          <span class="material-symbols-outlined text-lg">link</span>
          {t(submitting.value ? `${NS}.pairing` : `${NS}.pair_again`)}
        </button>
      </div>
    );

    const renderBody = () => {
      if (isEdit.value && props.terminal) {
        return (
          <>
            <TerminalSummary
              terminal={props.terminal}
              integrationLabel={integrationLabel.value}
            />
            {canRepair.value && renderRepair()}
            {renderNameField()}
            {renderFields(singleFormFields.value)}
            {renderStaff()}
          </>
        );
      }

      if (isWizard.value) {
        const current = config.value.steps[step.value];
        return (
          <>
            <ul class="steps w-full">
              {config.value.steps.map((s, i) => (
                <li key={s.titleKey} class={`step ${step.value >= i ? "step-primary" : ""}`}>
                  {t(s.titleKey)}
                </li>
              ))}
            </ul>
            {step.value === 0 ? (
              <>
                <ProviderPicker
                  value={provider.value}
                  onChange={(p: TerminalProvider) => {
                    provider.value = p;
                  }}
                />
                {isPairing.value && renderIntegrationSelect()}
                {renderNameField()}
                {renderStaff()}
              </>
            ) : (
              <>
                {current.instructionKeys && renderInstructions(current.instructionKeys)}
                {renderFields(current.fields)}
              </>
            )}
          </>
        );
      }

      return (
        <>
          <ProviderPicker
            value={provider.value}
            onChange={(p: TerminalProvider) => {
              provider.value = p;
              step.value = 0;
            }}
          />
          {renderNameField()}
          {renderFields(singleFormFields.value)}
          {renderStaff()}
        </>
      );
    };

    const renderFooter = () => {
      const wizardHasNext =
        isWizard.value && step.value < config.value.steps.length - 1;
      const primaryLabel = submitting.value
        ? t(isEdit.value ? `${NS}.saving` : isPairing.value ? `${NS}.pairing` : `${NS}.creating`)
        : t(
            isEdit.value
              ? `${NS}.save_changes`
              : isPairing.value
                ? `${NS}.pair_terminal`
                : `${NS}.create_terminal`,
          );

      return (
        <div class="flex flex-wrap items-center gap-2 border-t border-base-300 bg-base-200/40 px-6 py-4">
          {isEdit.value && (
            <button
              type="button"
              class={`btn btn-sm ${confirmingDelete.value ? "btn-error" : "btn-ghost text-error"}`}
              disabled={submitting.value}
              onClick={remove}
            >
              <span class="material-symbols-outlined text-lg">
                {isPaired.value ? "link_off" : "delete"}
              </span>
              {t(removeLabelKey.value)}
            </button>
          )}
          <div class="ml-auto flex gap-2">
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              disabled={submitting.value}
              onClick={() =>
                isWizard.value && step.value > 0 ? step.value-- : props.onClose()
              }
            >
              {t(isWizard.value && step.value > 0 ? `${NS}.back` : `${NS}.cancel`)}
            </button>
            {wizardHasNext ? (
              <button
                type="button"
                class="btn btn-primary btn-sm"
                disabled={!detailsValid.value}
                onClick={() => step.value++}
              >
                {t(`${NS}.next`)}
                <span class="material-symbols-outlined text-lg">arrow_forward</span>
              </button>
            ) : (
              <button
                type="submit"
                class="btn btn-primary btn-sm"
                disabled={submitting.value || !canSubmit.value}
              >
                {submitting.value && <span class="loading loading-spinner loading-sm"></span>}
                {primaryLabel}
              </button>
            )}
          </div>
        </div>
      );
    };

    return () => (
      <RightSheet
        isOpen={true}
        width="520px"
        title={isEdit.value ? props.terminal?.name || t(`${NS}.terminal_fallback`) : t(`${NS}.add_terminal`)}
        onClose={props.onClose}
      >
        <form
          class="flex h-full flex-col"
          novalidate
          onSubmit={(e: Event) => {
            e.preventDefault();
            submit();
          }}
        >
          <div class="flex-1 space-y-6 overflow-y-auto px-6 py-6">
            {formError.value && (
              <div role="alert" class="alert alert-error text-sm">
                <span class="material-symbols-outlined">error</span>
                <span>{formError.value}</span>
              </div>
            )}
            {renderBody()}
          </div>
          {renderFooter()}
        </form>
      </RightSheet>
    );
  },
});
